import { Component, OnInit } from '@angular/core';
import { forkJoin } from 'rxjs';
import { BaseComponent } from 'src/app/core/abstracts/baseComponent';
import { CredentialTransitionManagementService } from 'src/app/core/services/credential-transition-management.service';
import { CredentialTransitionProcess, CredentialTransitionLookups } from 'src/app/models/credentialTransitionProcess';
import { Store } from '@ngrx/store';
import { State } from 'src/app/core/store';
import { AppPermission } from 'src/app/models/appEnums';

@Component({
  selector: 'app-credential-transitions',
  templateUrl: './credential-transitions.component.html',
  standalone: false
})
export class CredentialTransitionsComponent extends BaseComponent implements OnInit
{
  processes: CredentialTransitionProcess[] = [];
  lookups: CredentialTransitionLookups =
  {
    credentials: [],
    jobTitles: [],
    templates: []
  };
  selected: CredentialTransitionProcess;
  sameTitle = true;
  busy = false;
  transitionSaving = false;
  error = '';
  canManageProcesses = false;
  canViewTransitions = false;
  activeTab: 'processes' | 'employees';

  constructor(private service: CredentialTransitionManagementService, private store: Store<State>)
  {
    super();
  }

  ngOnInit()
  {
    this.subs.push(this.store.select(state => state.credentialTransitionsState.saving)
      .subscribe(saving => this.transitionSaving = saving));
    this.subs.push(this.store.select(state => state.accountState.user?.permissionIDs).subscribe(permissions =>
    {
      this.canManageProcesses = permissions?.includes(AppPermission.CredentialTransitionsManage);
      this.canViewTransitions = permissions?.includes(AppPermission.CredentialTransitionsView)
        || permissions?.includes(AppPermission.CredentialTransitionsEdit);
      if (!this.activeTab)
      {
        this.activeTab = this.canManageProcesses ? 'processes' : 'employees';
        if (this.canManageProcesses)
          this.load();
      }
    }));
  }

  load()
  {
    this.busy = true;

    this.subs.push(
      forkJoin(
        {
          processes: this.service.list(),
          lookups: this.service.lookups()
        }).subscribe(
          {
            next: result =>
            {
              this.processes = result.processes;
              this.lookups = result.lookups;
              this.busy = false;
            },
            error: e => this.fail(e)
          })
    );
  }

  create()
  {
    this.error = '';
    this.sameTitle = true;
    this.selected =
    {
      id: 0,
      versionNumber: 0,
      name: '',
      description: '',
      enabled: false,
      paused: false,
      archived: false,
      applyToExisting: false,
      sourceCredentialID: null,
      targetCredentialID: null,
      requireExamDate: false,
      requireCredentialUpload: false,
      requireKra: false,
      kraValidityDays: 30,
      reuseValidKra: true,
      compensationMode: 1,
      jobTitleID: null,
      clinicalJobTitleID: null,
      showSignaturePrompt: false,
      rates: []
    };
  }

  edit(process: CredentialTransitionProcess)
  {
    this.error = '';
    this.selected =
    {
      ...process,
      applyToExisting: false,
      rates: process.rates.map(r => ({ ...r }))
    };
    this.sameTitle = process.jobTitleID === process.clinicalJobTitleID;
  }

  save()
  {
    if (this.busy)
      return;

    const payload =
    {
      ...this.selected,
      clinicalJobTitleID: this.sameTitle ? this.selected.jobTitleID : this.selected.clinicalJobTitleID,
      requireKra: this.selected.requireKra || this.selected.compensationMode === 2,
      rates: this.selected.rates.map(r => ({ ...r }))
    };
    this.busy = true;
    this.error = '';

    this.subs.push(
      this.service.save(payload).subscribe(
        {
          next: () =>
          {
            this.selected = null;
            this.load();
          },
          error: e => this.fail(e)
        })
    );
  }

  control(process: CredentialTransitionProcess, control: 'enabled' | 'paused')
  {
    if (this.busy)
      return;

    this.busy = true;
    this.error = '';

    this.subs.push(
      this.service.controls(process, control).subscribe(
        {
          next: () => this.load(),
          error: e => this.fail(e)
        })
    );
  }

  remove(process: CredentialTransitionProcess)
  {
    if (this.busy
      || !window.confirm(`Delete ${process.name}? Processes with employee history will be archived instead.`))
      return;

    this.busy = true;
    this.error = '';

    this.subs.push(
      this.service.delete(process.id).subscribe(
        {
          next: () => this.load(),
          error: e => this.fail(e)
        })
    );
  }

  label(id: number, type: 'credentials' | 'jobTitles' | 'templates')
  {
    return this.lookups[type].find(x => x.id === id)?.name || `#${id}`;
  }

  fail(e: any)
  {
    this.error = typeof e?.error === 'string' ? e.error : e?.message || 'The request failed.';
    this.busy = false;
  }
}
